import { useCallback, useRef, type RefObject } from 'react'
import { SIDEBAR_ROW_MOVE_MS } from '@/styling/tokens.interaction'

const ROW_SELECTOR = '[data-sidebar-table-row]'

function rowOffsets(container: HTMLElement): Map<string, number> {
  const containerTop = container.getBoundingClientRect().top
  const offsets = new Map<string, number>()
  container.querySelectorAll<HTMLElement>(ROW_SELECTOR).forEach((row) => {
    const id = row.dataset.sidebarTableRow
    if (id) offsets.set(id, row.getBoundingClientRect().top - containerTop)
  })
  return offsets
}

/**
 * Glides table rows from where they were to where a re-sort put them. Call
 * `capture` just before the change that re-sorts, and `play` in a layout
 * effect once it has rendered.
 */
export function useRowMoveAnimation(containerRef: RefObject<HTMLElement | null>) {
  const capturedRef = useRef<Map<string, number> | null>(null)

  const capture = useCallback(() => {
    const container = containerRef.current
    capturedRef.current = container ? rowOffsets(container) : null
  }, [containerRef])

  const play = useCallback(() => {
    const before = capturedRef.current
    capturedRef.current = null
    const container = containerRef.current
    if (!before || !container) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return

    for (const [id, offset] of rowOffsets(container)) {
      const previous = before.get(id)
      const row = container.querySelector<HTMLElement>(`[data-sidebar-table-row="${id}"]`)
      if (previous === undefined || !row || typeof row.animate !== 'function') continue
      const distance = previous - offset
      if (Math.abs(distance) < 1) continue
      row.animate([{ transform: `translateY(${distance}px)` }, { transform: 'translateY(0)' }], {
        duration: SIDEBAR_ROW_MOVE_MS,
        easing: 'ease-out',
      })
    }
  }, [containerRef])

  return { capture, play }
}
